import { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { Box, Button, Chip, Link, Typography } from "@mui/material";
import { JobDetailAssignment, JobDetailConfirmation, JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard } from "./DetailCard";
import VendorContactBlock from "./VendorContactBlock";
import VendorContactModal from "./VendorContactModal";
import { EventType, Timeline } from "./Timeline";

type Props = {
  job: JobDetailViewModel;
};

function ContactBlock({ title, name, email, phone }: { title: string; name?: string; email?: string; phone?: string }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5, fontWeight: 700, letterSpacing: 0.6 }}
      >
        {title}
      </Typography>
      <Typography fontWeight={600} sx={{ fontSize: 14 }}>
        {name || "—"}
      </Typography>
      {email ? (
        <Link href={`mailto:${email}`} sx={{ fontSize: 13, display: "block" }}>
          {email}
        </Link>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
          —
        </Typography>
      )}
      {phone && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
          {phone}
        </Typography>
      )}
    </Box>
  );
}

export default function ScheduledPickupCard({ job }: Props) {
  const assignment = job.currentAssignment as JobDetailAssignment;
  const confirmation = job.confirmation as JobDetailConfirmation;
  const [editingPrimaryContact, setEditingPrimaryContact] = useState(false);
  const [primaryContact, setPrimaryContact] = useState({
    contactName: assignment.vendorContactName,
    email: assignment.vendorEmail,
    phone: assignment.vendorPhone,
  });

  return (
    <DetailCard title="Scheduled Pickup">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#E8F5E9",
          borderRadius: "8px",
          px: 2,
          py: 1.25,
          mb: 2.5,
        }}
      >
        <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 20 }} />
        <Typography fontWeight={700} sx={{ fontSize: 13, letterSpacing: 0.4, color: "#1B5E20" }}>
          {confirmation.confirmedForLabel}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            backgroundColor: assignment.color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {assignment.initial}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
            <Typography fontWeight={700} sx={{ fontSize: 16 }}>
              {assignment.vendorName}
            </Typography>
            <Chip
              label="CONFIRMED"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                backgroundColor: "#E8F5E9",
                color: "#2E7D32",
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            {assignment.vendorType}
          </Typography>
        </Box>
        <Typography fontWeight={700} sx={{ fontSize: 15, flexShrink: 0 }}>
          {confirmation.lockedInPriceLabel}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          py: 2,
          borderTop: "1px solid #ECECEC",
          borderBottom: "1px solid #ECECEC",
          mb: 2.5,
        }}
      >
        <VendorContactBlock
          title="PRIMARY CONTACT"
          name={primaryContact.contactName}
          email={primaryContact.email}
          phone={primaryContact.phone}
          onEdit={() => setEditingPrimaryContact(true)}
        />
        <ContactBlock
          title="CUSTOMER CONTACT"
          name={confirmation.customerName}
          email={confirmation.customerEmail}
          phone={confirmation.customerPhone}
        />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Timeline
          events={[
            {
              eventName: "Vendor accepted",
              date: confirmation.vendorAcceptedAt,
              type: EventType.CONFIRMED,
              url: confirmation.vendorPortalUrl,
            },
            {
              eventName: "Customer confirmed",
              date: confirmation.customerConfirmedAt,
              type: EventType.CONFIRMED,
              url: confirmation.customerPortalUrl,
            },
            {
              eventName: confirmation.crewConfirmedAt ? "Crew confirmed" : "Awaiting crew confirmation",
              date: confirmation.crewConfirmedAt,
              type: confirmation.crewConfirmedAt ? EventType.CONFIRMED : EventType.AWAITING,
              url: confirmation.crewPortalUrl,
            },
          ]}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          href={primaryContact.email ? `mailto:${primaryContact.email}` : undefined}
          disabled={!primaryContact.email}
          sx={{ textTransform: "uppercase", fontWeight: 700, fontSize: 12 }}
        >
          Email Vendor
        </Button>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          href={confirmation.customerEmail ? `mailto:${confirmation.customerEmail}` : undefined}
          disabled={!confirmation.customerEmail}
          sx={{ textTransform: "uppercase", fontWeight: 700, fontSize: 12 }}
        >
          Email Customer
        </Button>
      </Box>

      <VendorContactModal
        open={editingPrimaryContact}
        setOpen={setEditingPrimaryContact}
        vendorUuid={assignment.vendorId}
        vendorName={assignment.vendorName}
        contactName={primaryContact.contactName}
        email={primaryContact.email}
        phone={primaryContact.phone}
        onSaved={(vendor) =>
          setPrimaryContact({
            contactName: vendor.contactName,
            email: vendor.email,
            phone: vendor.phone,
          })
        }
      />
    </DetailCard>
  );
}
